/**
 * Arabic numerals ⇄ Chinese numerals.
 *
 * Rules implemented:
 *
 * - Chinese groups digits in fours, not threes: 万 (10^4) and 亿 (10^8) open a new group, so
 *   123456789 is 一亿二千三百四十五万六千七百八十九 (1,2345,6789). Beyond 亿 the PRC says 万亿
 *   (10^12) and 亿亿 (10^16); the classical ladder is 兆 (10^12), 京 (10^16), 垓 (10^20).
 * - 零 is written once for any run of zeros between non-zero digits, including across a
 *   group boundary or a skipped group: 一百零一, 一万零一百, 一亿零五千. Trailing zeros are
 *   not written: 一万, not 一万零.
 * - 一 is dropped before a leading 十 (十五) but kept elsewhere (一百一十五). In financial
 *   (大写) numerals it is always written: 壹拾伍.
 * - 两 may stand for 二 before 百, 千, 万 and 亿 in speech (两百, 两千, 两万).
 * - Financial numerals (大写) use 壹贰叁肆伍陆柒捌玖拾佰仟 so that no stroke can be added to
 *   a cheque; RMB amounts end in 元整 when there are no 角 or 分, use 角 for tenths and 分
 *   for hundredths, write 零 between 元 and a lone 分, and are preceded by 人民币 (People's
 *   Bank of China rules for filling in bills and settlement documents).
 * - Reading goes the other way: a bare digit after a unit means the next unit down in
 *   colloquial Chinese (一百二 = 120, 三万五 = 35 000); 零 switches that off (三万零五 = 30 005).
 */

import { isWholeDigits, splitGroups } from "./number-input";

export type ChineseScript = "simplified" | "traditional";
export type ChineseStyle = "standard" | "financial";
export type ChineseForm = "full" | "digits" | "amount";
export type ChineseLargeUnits = "compound" | "ladder";

export interface ChineseOptions {
  script: ChineseScript;
  style: ChineseStyle;
  form: ChineseForm;
  /** Use 两 for 二 before 百, 千, 万 and 亿. */
  liang: boolean;
  units: ChineseLargeUnits;
  /** In digit-by-digit form write zero as 〇 instead of 零. */
  circleZero: boolean;
}

export type ChineseNote =
  | "fourDigitGroups"
  | "zero"
  | "tenOmitsOne"
  | "financial"
  | "liang"
  | "amount"
  | "largeUnits";

export interface ChineseSpelled {
  words: string;
  /** The digits regrouped in fours with their units: "12亿3456万7890". */
  mixed: string | null;
  notes: ChineseNote[];
}

const DIGITS_STANDARD = "零一二三四五六七八九";
const DIGITS_FINANCIAL_SIMPLIFIED = "零壹贰叁肆伍陆柒捌玖";
const DIGITS_FINANCIAL_TRADITIONAL = "零壹貳叁肆伍陸柒捌玖";

function digitChars(options: ChineseOptions): string {
  if (options.style === "standard") {
    return DIGITS_STANDARD;
  }
  return options.script === "simplified"
    ? DIGITS_FINANCIAL_SIMPLIFIED
    : DIGITS_FINANCIAL_TRADITIONAL;
}

/** The place names within a four-digit group, from 千 down to the units. */
function placeChars(style: ChineseStyle): string[] {
  return style === "standard" ? ["千", "百", "十", ""] : ["仟", "佰", "拾", ""];
}

function largeUnits(options: ChineseOptions): string[] {
  const wan = options.script === "simplified" ? "万" : "萬";
  const yi = options.script === "simplified" ? "亿" : "億";
  if (options.units === "ladder") {
    return ["", wan, yi, "兆", "京", "垓"];
  }
  return ["", wan, yi, `${wan}${yi}`, `${yi}${yi}`, `${wan}${yi}${yi}`];
}

interface GroupState {
  started: boolean;
  /** A whole group of zeros was skipped since the last group written. */
  gap: boolean;
}

function liangChar(options: ChineseOptions): string {
  return options.script === "simplified" ? "两" : "兩";
}

/** The character for a digit 1–9 at a place in a group (0 千, 1 百, 2 十, 3 units). */
function digitFor(
  d: number,
  place: number,
  value: number,
  index: number,
  options: ChineseOptions
): string {
  const useLiang =
    options.liang &&
    options.style === "standard" &&
    d === 2 &&
    (place <= 1 || (place === 3 && index > 0 && value === 2));
  return useLiang ? liangChar(options) : digitChars(options)[d];
}

/** One 4-digit group (1–9999) as words, updating the running state. */
function groupWords(
  value: number,
  index: number,
  options: ChineseOptions,
  state: GroupState
): string {
  const zero = digitChars(options)[0];
  const places = placeChars(options.style);
  const text = String(value).padStart(4, "0");
  let out = "";
  let pending = state.started && (state.gap || value < 1000);
  for (let place = 0; place < 4; place++) {
    const d = Number(text[place]);
    if (d === 0) {
      pending = pending || out !== "";
      continue;
    }
    if (pending) {
      out += zero;
      pending = false;
    }
    const dropOne =
      d === 1 && place === 2 && !state.started && options.style === "standard";
    if (!dropOne) {
      out += digitFor(d, place, value, index, options);
    }
    out += places[place];
    state.started = true;
  }
  return out;
}

/** A whole number as full Chinese numerals. */
function fullWords(digits: string, options: ChineseOptions): string {
  if (digits === "0") {
    return digitChars(options)[0];
  }
  const groups = splitGroups(digits, 4);
  const units = largeUnits(options);
  const state: GroupState = { started: false, gap: false };
  let out = "";
  for (let i = groups.length - 1; i >= 0; i--) {
    const value = groups[i];
    if (value === 0) {
      state.gap = state.started;
      continue;
    }
    out += groupWords(value, i, options, state) + units[i];
    state.gap = false;
  }
  return out;
}

/** Each digit on its own: 二〇二六. */
function digitByDigit(digits: string, options: ChineseOptions): string {
  const chars = digitChars(options);
  const zero =
    options.circleZero && options.style === "standard" ? "〇" : chars[0];
  return [...digits].map((d) => (d === "0" ? zero : chars[Number(d)])).join("");
}

/** 12亿3456万7890: Arabic digits regrouped in fours under their Chinese units. */
function mixedForm(digits: string, options: ChineseOptions): string | null {
  if (digits.length <= 4) {
    return null;
  }
  const groups = splitGroups(digits, 4);
  const units = largeUnits(options);
  let out = "";
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] > 0) {
      out += `${groups[i]}${units[i]}`;
    }
  }
  return out;
}

const NON_ZERO_DIGIT = /[1-9]/;

function collectNotes(
  digits: string,
  words: string,
  options: ChineseOptions
): ChineseNote[] {
  const notes: ChineseNote[] = [];
  const zero = digitChars(options)[0];
  if (digits.length > 4) {
    notes.push("fourDigitGroups");
  }
  if (options.form === "full" && words.includes(zero) && digits !== "0") {
    notes.push("zero");
  }
  if (options.form === "full" && words.startsWith("十")) {
    notes.push("tenOmitsOne");
  }
  if (options.style === "financial") {
    notes.push("financial");
  }
  if (words.includes(liangChar(options)) && options.liang) {
    notes.push("liang");
  }
  if (digits.length > 12 && options.form === "full") {
    notes.push("largeUnits");
  }
  return notes;
}

export interface ChineseAmountParts {
  integer: string;
  jiao: number;
  fen: number;
}

/** Read the fraction as whole jiao and fen; null when it has a digit below the fen. */
export function splitAmount(
  integer: string,
  fraction: string
): ChineseAmountParts | null {
  if (NON_ZERO_DIGIT.test(fraction.slice(2))) {
    return null;
  }
  const padded = `${fraction}00`;
  return { integer, jiao: Number(padded[0]), fen: Number(padded[1]) };
}

/** A capital RMB amount: 壹佰元整, 壹佰元零伍分, 伍角伍分 (the caller adds 人民币). */
function amountWords(
  parts: ChineseAmountParts,
  options: ChineseOptions
): string {
  const digits = digitChars(options);
  const { integer, jiao, fen } = parts;
  const yuan = integer === "0" ? "" : `${fullWords(integer, options)}元`;
  if (jiao === 0 && fen === 0) {
    return integer === "0" ? `${digits[0]}元整` : `${yuan}整`;
  }
  const jiaoText = jiao > 0 ? `${digits[jiao]}角` : "";
  const fenText = fen > 0 ? `${digits[fen]}分` : "";
  const gap = yuan !== "" && jiao === 0 ? digits[0] : "";
  return `${yuan}${gap}${jiaoText}${fenText}`;
}

/** The words for the chosen form, without the sign. */
function spellBody(
  integer: string,
  fraction: string,
  options: ChineseOptions
): string {
  const point = options.script === "simplified" ? "点" : "點";
  const hasFraction = fraction !== "" && NON_ZERO_DIGIT.test(fraction);
  if (options.form === "amount") {
    const parts = splitAmount(integer, fraction);
    if (parts === null) {
      throw new RangeError("chineseSpell: amount has more than two decimals");
    }
    const rmb = options.script === "simplified" ? "人民币" : "人民幣";
    return `${rmb}${amountWords(parts, options)}`;
  }
  const whole =
    options.form === "digits"
      ? digitByDigit(integer, options)
      : fullWords(integer, options);
  if (!hasFraction) {
    return whole;
  }
  const tail = digitByDigit(fraction, {
    ...options,
    circleZero: options.form === "digits" && options.circleZero,
  });
  return whole + point + tail;
}

/**
 * Spell a signed, possibly decimal number. For the amount form the fraction must fit in
 * two places — check `splitAmount` first.
 */
export function chineseSpell(
  negative: boolean,
  integer: string,
  fraction: string,
  options: ChineseOptions
): ChineseSpelled {
  if (!isWholeDigits(integer)) {
    throw new RangeError("chineseSpell: out of range");
  }
  const effective: ChineseOptions =
    options.form === "amount" ? { ...options, style: "financial" } : options;
  const minus = effective.script === "simplified" ? "负" : "負";
  const words = spellBody(integer, fraction, effective);
  const notes = collectNotes(integer, words, effective);
  if (effective.form === "amount") {
    notes.push("amount");
  }
  return {
    words: negative ? minus + words : words,
    mixed: effective.form === "full" ? mixedForm(integer, effective) : null,
    notes,
  };
}

// ---------------------------------------------------------------------------
// Reading Chinese numerals back into digits

export type ChineseReadError = "empty" | "invalid" | "order" | "tooBig";

export type ChineseReadNote =
  | "colloquial"
  | "digitByDigit"
  | "financial"
  | "amount";

export type ChineseReadResult =
  | {
      ok: true;
      negative: boolean;
      integer: string;
      fraction: string;
      notes: ChineseReadNote[];
    }
  | { ok: false; error: ChineseReadError };

type Token =
  | { kind: "digit"; value: number }
  | { kind: "unit"; power: number }
  | { kind: "big"; power: number };

const DIGIT_CHARS: Record<string, number> = {
  〇: 0,
  "○": 0,
  零: 0,
  一: 1,
  壹: 1,
  二: 2,
  贰: 2,
  貳: 2,
  两: 2,
  兩: 2,
  三: 3,
  叁: 3,
  參: 3,
  四: 4,
  肆: 4,
  五: 5,
  伍: 5,
  六: 6,
  陆: 6,
  陸: 6,
  七: 7,
  柒: 7,
  八: 8,
  捌: 8,
  九: 9,
  玖: 9,
};

const UNIT_CHARS: Record<string, number> = {
  十: 1,
  拾: 1,
  百: 2,
  佰: 2,
  千: 3,
  仟: 3,
};

const BIG_CHARS: Record<string, number> = {
  万: 4,
  萬: 4,
  亿: 8,
  億: 8,
  兆: 12,
  京: 16,
  垓: 20,
};

const EXPAND: Record<string, string> = { 廿: "二十", 卅: "三十" };
const STRIP = /[\s,，、]/g;
const ARABIC_DIGIT = /[0-9０-９]/;
const NEGATIVE_START = /^[-−负負]/;
const POINT = /[点點.．]/;
const AMOUNT_MARKS = /[元圆圓角分]/;
const AMOUNT_PREFIX = /^人民[币幣]/;
const AMOUNT_END = /[整正]$/;
const LEADING_ZEROS = /^0+(?=\d)/;
const LEADING_LING = /^零/;
const JIAO = /^(.)角/;
const FEN = /^零?(.)分$/;

/** Mutable flags collected while reading one numeral. */
interface ReadContext {
  colloquial: boolean;
}

/** 0–9 for an ASCII or full-width digit. */
function arabicValue(c: string): number {
  const code = c.charCodeAt(0);
  return code >= 0xff_10 ? code - 0xff_10 : code - 48;
}

/** Adjacent large units multiply: 万亿 is 10^12 and 亿亿 is 10^16. */
function pushBig(tokens: Token[], power: number): void {
  const last = tokens.at(-1);
  if (last?.kind === "big") {
    last.power += power;
  } else {
    tokens.push({ kind: "big", power });
  }
}

function tokenize(text: string): Token[] | null {
  const tokens: Token[] = [];
  for (const raw of text) {
    const char = EXPAND[raw] ?? raw;
    for (const c of char) {
      const unit = UNIT_CHARS[c];
      const big = BIG_CHARS[c];
      const digit = ARABIC_DIGIT.test(c) ? arabicValue(c) : DIGIT_CHARS[c];
      if (unit !== undefined) {
        tokens.push({ kind: "unit", power: unit });
      } else if (big !== undefined) {
        pushBig(tokens, big);
      } else if (digit !== undefined) {
        tokens.push({ kind: "digit", value: digit });
      } else {
        return null;
      }
    }
  }
  return tokens;
}

function addDigits(a: string, b: string): string {
  let carry = 0;
  let out = "";
  for (let i = 0; i < Math.max(a.length, b.length) || carry > 0; i++) {
    const sum = Number(a.at(-1 - i) ?? 0) + Number(b.at(-1 - i) ?? 0) + carry;
    out = (sum % 10) + out;
    carry = sum >= 10 ? 1 : 0;
  }
  return out === "" ? "0" : out.replace(LEADING_ZEROS, "");
}

function shift(digits: string, power: number): string {
  return digits === "0" ? "0" : digits + "0".repeat(power);
}

type Parsed = { digits: string } | { error: ChineseReadError };

interface SectionState {
  value: number;
  pending: number | null;
  lastPower: number;
  zeroAfterUnit: boolean;
  sawUnit: boolean;
}

function stepDigit(
  state: SectionState,
  digit: number
): ChineseReadError | null {
  if (digit === 0) {
    if (state.pending !== null) {
      return "invalid";
    }
    state.zeroAfterUnit = true;
    return null;
  }
  if (state.pending !== null) {
    return "invalid";
  }
  state.pending = digit;
  return null;
}

function stepUnit(state: SectionState, power: number): ChineseReadError | null {
  if (power >= state.lastPower) {
    return "order";
  }
  state.value += (state.pending ?? 1) * 10 ** power;
  state.pending = null;
  state.lastPower = power;
  state.sawUnit = true;
  state.zeroAfterUnit = false;
  return null;
}

/** Tokens made only of digits and the units 十百千: a value from 0 to 9999. */
function parseSection(tokens: Token[], ctx: ReadContext): Parsed {
  const state: SectionState = {
    value: 0,
    pending: null,
    lastPower: 4,
    zeroAfterUnit: false,
    sawUnit: false,
  };
  for (const token of tokens) {
    let error: ChineseReadError | null = "invalid";
    if (token.kind === "digit") {
      error = stepDigit(state, token.value);
    } else if (token.kind === "unit") {
      error = stepUnit(state, token.power);
    }
    if (error !== null) {
      return { error };
    }
  }
  if (state.pending === null) {
    return { digits: String(state.value) };
  }
  // A bare digit after a unit is the next unit down (一百二 = 120) unless a 零 intervenes.
  const colloquial =
    state.sawUnit && !state.zeroAfterUnit && state.lastPower > 1;
  ctx.colloquial = ctx.colloquial || colloquial;
  const place = colloquial ? 10 ** (state.lastPower - 1) : 1;
  return { digits: String(state.value + state.pending * place) };
}

function highestBig(tokens: Token[]): number {
  let best = -1;
  let power = 0;
  tokens.forEach((token, index) => {
    if (token.kind === "big" && token.power >= power) {
      power = token.power;
      best = index;
    }
  });
  return best;
}

/** The right-hand side of a big unit, with the colloquial single digit (三万五). */
function parseRemainder(
  right: Token[],
  power: number,
  ctx: ReadContext
): Parsed {
  if (right.length === 0) {
    return { digits: "0" };
  }
  const only = right[0];
  if (right.length === 1 && only.kind === "digit" && only.value > 0) {
    ctx.colloquial = true;
    return { digits: shift(String(only.value), power - 1) };
  }
  return parseTokens(right, ctx);
}

function parseTokens(tokens: Token[], ctx: ReadContext): Parsed {
  const split = highestBig(tokens);
  if (split < 0) {
    return parseSection(tokens, ctx);
  }
  const big = tokens[split];
  if (big.kind !== "big") {
    return { error: "invalid" };
  }
  const left =
    split === 0 ? { digits: "1" } : parseTokens(tokens.slice(0, split), ctx);
  if ("error" in left) {
    return left;
  }
  const right = parseRemainder(tokens.slice(split + 1), big.power, ctx);
  if ("error" in right) {
    return right;
  }
  if (right.digits.length > big.power && right.digits !== "0") {
    return { error: "order" };
  }
  return { digits: addDigits(shift(left.digits, big.power), right.digits) };
}

/** True when every token is a bare digit: a positional reading such as 二〇二六. */
function isPositional(tokens: Token[]): boolean {
  return tokens.length > 1 && tokens.every((t) => t.kind === "digit");
}

function readWhole(tokens: Token[], notes: ChineseReadNote[]): Parsed {
  if (isPositional(tokens)) {
    notes.push("digitByDigit");
    return {
      digits: tokens.map((t) => (t.kind === "digit" ? t.value : 0)).join(""),
    };
  }
  const ctx: ReadContext = { colloquial: false };
  const parsed = parseTokens(tokens, ctx);
  if (ctx.colloquial) {
    notes.push("colloquial");
  }
  return parsed;
}

function readFraction(text: string): string | null {
  const tokens = tokenize(text);
  if (tokens === null || tokens.some((t) => t.kind !== "digit")) {
    return null;
  }
  return tokens.map((t) => (t.kind === "digit" ? t.value : 0)).join("");
}

/** Read a Chinese amount such as 壹佰元伍角伍分 into integer and two decimals. */
function readAmount(
  body: string
): { integer: string; fraction: string } | null {
  const yuanAt = [...body].findIndex((c) => "元圆圓".includes(c));
  const whole = yuanAt < 0 ? "" : body.slice(0, yuanAt);
  const rest = yuanAt < 0 ? body : body.slice(yuanAt + 1);
  let integer = "0";
  if (whole !== "") {
    const tokens = tokenize(whole);
    const parsed: Parsed =
      tokens === null
        ? { error: "invalid" }
        : parseTokens(tokens, { colloquial: false });
    if ("error" in parsed) {
      return null;
    }
    integer = parsed.digits;
  }
  const tail = rest.replace(AMOUNT_END, "").replace(LEADING_LING, "");
  const jiao = JIAO.exec(tail);
  const fenSource = jiao === null ? tail : tail.slice(2);
  const fen = FEN.exec(fenSource);
  const leftover = fen === null ? fenSource : "";
  const parts = [jiao?.[1], fen?.[1]].map((c) =>
    c === undefined ? 0 : DIGIT_CHARS[c]
  );
  const bad = leftover !== "" || parts.some((p) => p === undefined);
  return bad ? null : { integer, fraction: `${parts[0]}${parts[1]}` };
}

interface PlainRead {
  integer: string;
  fraction: string;
  notes: ChineseReadNote[];
}

/** A numeral without sign or currency: whole part, optional decimal tail. */
function readPlain(body: string): PlainRead | ChineseReadError {
  const [wholeText = "", ...fractionParts] = body.split(POINT);
  const tokens = tokenize(wholeText);
  if (tokens === null || tokens.length === 0 || fractionParts.length > 1) {
    return "invalid";
  }
  const notes: ChineseReadNote[] = [];
  const parsed = readWhole(tokens, notes);
  if ("error" in parsed) {
    return parsed.error;
  }
  const fraction =
    fractionParts.length === 1 ? readFraction(fractionParts[0]) : "";
  if (fraction === null) {
    return "invalid";
  }
  if (parsed.digits.length > 24) {
    return "tooBig";
  }
  return {
    integer: parsed.digits.replace(LEADING_ZEROS, ""),
    fraction,
    notes,
  };
}

function readAmountText(body: string): PlainRead | ChineseReadError {
  const amount = readAmount(body.replace(AMOUNT_PREFIX, ""));
  if (amount === null) {
    return "invalid";
  }
  if (amount.integer.length > 24) {
    return "tooBig";
  }
  return { ...amount, notes: ["amount", "financial"] };
}

/** Read Chinese (or mixed Arabic-and-Chinese) numerals into digits. */
export function chineseRead(text: string): ChineseReadResult {
  const stripped = text.replace(STRIP, "");
  if (stripped === "") {
    return { ok: false, error: "empty" };
  }
  const negative = NEGATIVE_START.test(stripped);
  const body = negative ? stripped.slice(1) : stripped;
  const read = AMOUNT_MARKS.test(body) ? readAmountText(body) : readPlain(body);
  if (typeof read === "string") {
    return { ok: false, error: read };
  }
  const isZero = read.integer === "0" && !NON_ZERO_DIGIT.test(read.fraction);
  return { ok: true, negative: negative && !isZero, ...read };
}
